import { replace } from 'connected-react-router/immutable';
import { Observable } from 'rxjs';
import _ from 'lodash';
import * as Sentry from '@sentry/react';
import type { ActionsObservable } from 'redux-observable';
import type { Action, Store } from 'redux';
import { LOCATION_CHANGE } from 'connected-react-router';

import type { State } from 'reducers';
import { logoutFailedAction } from 'containers/LoginPage/actions';
import { LOGOUT } from 'containers/LoginPage/constants';
import { RELOAD_ALL_TABS, RELOAD_CURRENT_TAB } from 'broadcastChannels/constants';
import config from 'config';
import AuthApi from 'entities/auth/api';
import { storeAction } from 'entities/auth/actions';
import { REFETCH_USER, STORE as STORE_AUTH } from 'entities/auth/constants';
import UserApi from 'entities/user/api';
import PartnersApi from 'entities/partners/api';
import Api from 'entities/api';
import { dispatchMessage } from 'broadcastChannels/deed';
import { reloadCurrentTabAction } from 'broadcastChannels/actions';
import { setLocalSettingAction } from 'localSettings/actions';

const configureApiHeaders = (action$: ActionsObservable<Action>) =>
  action$.ofType('persist/REHYDRATE').mergeMap(() =>
    Observable.from(
      fetch(`/favicon.ico`, {
        method: 'HEAD',
      })
    )
      .mergeMap((response) => {
        const viewerCountry =
          response.headers.get('cloudfront-viewer-country') ??
          ((config.isProduction && 'QPS') || undefined);
        const viewerTimezone =
          response.headers.get('cloudfront-viewer-time-zone') ??
          Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone ??
          'America/New_York';

        Api.defaultHeaders['Viewer-Country'] = viewerCountry;

        return [
          setLocalSettingAction('viewerCountry', viewerCountry),
          setLocalSettingAction('timeZone', viewerTimezone),
        ];
      })
      .catch((error) => {
        Sentry.captureException(error, { level: 'warning' });

        return [];
      })
  );

// Set a `setAuthorization` function which gets called in case an api response contains an authorization token
const configureApiSetAuthorization = (action$: ActionsObservable<Action>, store$: Store<State>) =>
  action$.ofType('persist/REHYDRATE').mergeMap(() => {
    Api.setAuthorization = (tokenInner) => {
      Api.defaultHeaders.Authorization = `Bearer ${tokenInner}`;
      store$.dispatch(storeAction(tokenInner));
    };
    return [];
  });

const redirectIfAuthenticated = (action$: ActionsObservable<Action>) =>
  action$.ofType('persist/REHYDRATE').mergeMap(({ payload }) => {
    const token = payload.entities && payload.entities.auth.token;
    if (token) {
      return [storeAction(token)];
    }
    return [];
  });

const setApiDeedPathHeaderOnLocationChange = (action$: ActionsObservable<Action>) =>
  action$.ofType(LOCATION_CHANGE).mergeMap(
    ({
      payload: {
        location: { pathname, search },
      },
    }) => {
      Api.defaultHeaders['X-DEED-PATH'] = `${pathname}${search}`; // don't include hash for security reasons
      return [];
    }
  );

const performLogoutCleanup = () => {
  Api.LOGGING_OUT = true;
  Api.defaultHeaders = {};
  return [{ type: 'RESET' }, replace('/login')];
};

const logout = (action$: ActionsObservable<Action>) =>
  action$.ofType(LOGOUT).mergeMap(() =>
    AuthApi.logoutLocal()
      .mergeMap(() => performLogoutCleanup())
      .catch((e) => Observable.of(logoutFailedAction(e)))
  );

const handleFailedAuth = (action$: ActionsObservable<Action>) =>
  action$.ofType('LOGOUT_CALLBACK').mergeMap(() => performLogoutCleanup());

const afterAuthentication = (action$: ActionsObservable<Action>, store$: Store<State>) =>
  action$.ofType(STORE_AUTH).mergeMap(({ token }) => {
    Api.defaultHeaders.Authorization = `Bearer ${token}`;
    const parameters = _.fromPairs(
      _.compact(
        _.map(
          window.location.search.slice(1).split('&'),
          (parameter) => parameter && parameter.split('=')
        )
      )
    );

    Api.handleFailedAuth = () =>
      store$.dispatch({
        type: 'LOGOUT_CALLBACK',
      });

    return UserApi.fetch()
      .mergeMap((action) => {
        if (action.user.type !== 'Organizer' && action.user.type !== 'Admin') {
          return performLogoutCleanup();
        }
        if (action.user.organization?.id) {
          Api.defaultHeaders['X-DEED-ORGANIZATION'] = action.user.organization.id;
        }

        // Handle partner affiliation in the background if partnerToken parameter exists
        if (parameters.partnerToken && typeof parameters.partnerToken === 'string') {
          // Remove the partnerToken from the URL
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.delete('partnerToken');
          window.history.replaceState({}, document.title, currentUrl.href);

          // Make the partner token API call in the background
          PartnersApi.createWithToken(parameters.partnerToken).subscribe({
            error: (error) => {
              Sentry.captureException(error, { level: 'error' });
              console.error('Failed to create partner for token:', error);
            },
          });
        }

        return [action, parameters.d && replace(decodeURIComponent(parameters.d))].filter(Boolean);
      })
      .catch((error) => [{ type: 'USER_FETCH_FAILED', error }, ...performLogoutCleanup()]);
  });

const sendReloadAllTabsEvent = (action$: ActionsObservable<Action>) =>
  action$.ofType(RELOAD_ALL_TABS).mergeMap(() => {
    dispatchMessage('reloadAllTabs');
    // @NOTE-DP: after we have dispatch the reload message, we also need to reload the current tab
    return [reloadCurrentTabAction()];
  });

const reloadTab = (action$: ActionsObservable<Action>) =>
  action$.ofType(RELOAD_CURRENT_TAB).mergeMap(() => {
    // Get the current URL
    const currentUrl = new URL(window.location.href);

    // Check if there are search parameters
    if (currentUrl.search) {
      // Remove the search parameters
      currentUrl.search = '';

      // Update the URL without search parameters
      window.history.replaceState({}, document.title, currentUrl.href);
    }

    // Reload the page
    window.location.reload();

    return [];
  });

const refetchUser = (action$: ActionsObservable<Action>) =>
  action$
    .ofType(REFETCH_USER)
    .mergeMap(() =>
      UserApi.fetch().catch((error) => [
        { type: 'USER_FETCH_FAILED', error },
        ...performLogoutCleanup(),
      ])
    );

export default [
  configureApiHeaders,
  configureApiSetAuthorization,
  redirectIfAuthenticated,
  setApiDeedPathHeaderOnLocationChange,
  afterAuthentication,
  logout,
  handleFailedAuth,
  sendReloadAllTabsEvent,
  reloadTab,
  refetchUser,
];
